import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { furnitureService, FurnitureData } from '../../services/furniture';
import FurnitureForm from '../../components/furniture/FurnitureForm';
import { useTranslation } from 'react-i18next';

const EditFurniture: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [furniture, setFurniture] = useState<FurnitureData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFurniture();
  }, [id]);

  const fetchFurniture = async () => {
    try {
      const response = await furnitureService.getFurnitureById(id!);
      setFurniture(response.furniture);
    } catch (err: any) {
      setError(err.response?.data?.message || t('furniture.edit.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: Partial<FurnitureData>) => {
    try {
      await furnitureService.updateFurniture(id!, data);
      // Navigation will be handled by FurnitureForm component
    } catch (error) {
      console.error('Error updating furniture:', error);
      setError(t('furniture.edit.saveError'));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (!furniture) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold text-gray-900">{t('furniture.edit.notFound')}</h2>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('furniture.edit.title')}</h1>
      <FurnitureForm
        initialData={furniture}
        onSubmit={handleSubmit}
        isEditing
      />
    </div>
  );
};

export default EditFurniture;
